import lodash from 'lodash';
import BaseModel from '../BaseModel';
import type { ICredReq } from '$shared/interfaces/api/ICredReq';
import { CredReqType, CredReqTypeLabel, CredReqTypeMenuLabel } from '$shared/constants/CredReqType';
import type { CredReqPriority } from '$shared/constants/CredReqPriority';
import DateString from '../DateString';
import CredReqEvent from './CredReqEvent';
import User from '../User';
import {
  CompletedCvoStatusList,
  CredReqEventStatus,
  CredReqEventStatusLabel,
  CvoAdditionalInfoRequestedStatusList
} from '$shared/constants/CredReqEventStatus';
import type { ICheckListDetails } from '$shared/interfaces/api/ICheckListDetails';
import type { ICredReqEvent } from '$shared/interfaces/api/ICredReqEvent';

export type CvoDetailsInfo = {
  client: string;
  providerName: string;
  providerType: string;
  startDate: string;
  endDate: string;
  credentialingType: string;
};

export class CredReq<CL = any> extends BaseModel<ICredReq> {
  declare public id: string;
  declare public type: CredReqType;
  declare public priority: CredReqPriority;
  declare public createdAt: DateString;
  declare public currentEvent: CredReqEvent;
  declare public allEvents: CredReqEvent[];
  declare public dismissed: boolean;
  declare public isSelfManaged: boolean;
  declare public checklist: CL;
  declare public owner: User;
  declare public applicationReceivedDate: DateString | null;
  declare public notes: string | null;

  declare public providerType: string;

  constructor(response: ICredReq) {
    super(response);
  }

  public get parsedType(): string {
    return CredReqTypeLabel[this.type] || '';
  }

  public get currentEventTimestamp(): string {
    return this.currentEvent?.timestamp?.getParsedDate() || '';
  }

  public get currentEventStatus(): CredReqEventStatus {
    return this.currentEvent?.status;
  }

  public get parsedCreatedAt(): string {
    return this.createdAt?.getParsedDate() || '';
  }

  public get parsedCurrentStatus(): string {
    return this.currentStatus ? CredReqEventStatusLabel[this.currentStatus] : '';
  }

  public get currentStatus(): CredReqEventStatus {
    return this.currentEvent?.status;
  }

  private get requestKindLabel(): string {
    return this.isSelfManaged ? 'Self Managed Request' : 'CVO Request';
  }

  public get requestStatusLabel(): string {
    return `${this.requestKindLabel} Status`;
  }

  public get requestKindTitle(): string {
    return this.requestKindLabel;
  }

  public get menuTitle(): string {
    const base = `${CredReqTypeMenuLabel[this.type]} (${this.parsedCurrentStatus})`;
    return this.isSelfManaged ? base : `CVO: ${base}`;
  }

  public get ownerName(): string {
    return this.owner?.fullName || '';
  }

  public get deactivated(): boolean {
    return false;
  }

  public get isOffCycleType(): boolean {
    return this.type === CredReqType.OffCycle;
  }

  public get isReCredentialingType(): boolean {
    return this.type === CredReqType.ReCredentialing;
  }

  public get isProcessing(): boolean {
    return [
      CredReqEventStatus.RequestSubmitted,
      CredReqEventStatus.RequestInProgress,
      ...CvoAdditionalInfoRequestedStatusList,
      CredReqEventStatus.AdditionalInformationSubmitted,
      CredReqEventStatus.CancellationRequested
    ].includes(this.currentStatus);
  }

  public get sortedAllEvents(): CredReqEvent[] {
    return lodash.orderBy(
      this.allEvents,
      (event: CredReqEvent) => event.timestamp && new Date(event.timestamp.getRawString()),
      'desc'
    );
  }

  // #region Only for Table UI
  public get highlight(): boolean {
    return this.deactivated && !CompletedCvoStatusList.includes(this.currentStatus);
  }
  // #endregion

  public updatedResponseBase(response: ICredReq): void {
    this.response = response;
    this.transformResponse();
  }

  public updatedResponse(response: ICredReq): void {
    this.updatedResponseBase(response);
  }

  public toDetailInfo(): ICheckListDetails {
    return {};
  }

  protected transformResBase(): void {
    this.id = this.response.id;
    this.type = this.response.type;
    this.priority = this.response.priority;
    this.createdAt = new DateString(this.response.createdAt);
    this.currentEvent = new CredReqEvent(this.response.currentEvent);
    this.allEvents = this.response.allEvents?.map(
      (event: ICredReqEvent) => new CredReqEvent(event)
    );
    this.isSelfManaged = this.response.isSelfManaged;
    this.response.owner && (this.owner = new User(this.response.owner));
    this.applicationReceivedDate = DateString.create(this.response.applicationReceivedDate);
    this.notes = this.response.notes ?? null;
  }

  public static getIconByStatus(eventStatus: CredReqEventStatus): string {
    switch (eventStatus) {
      case CredReqEventStatus.RequestSubmitted:
      case CredReqEventStatus.AdditionalInformationSubmitted: {
        return 'icon-arrow-up-circle';
      }
      case CredReqEventStatus.RequestInProgress: {
        return 'icon-refresh-cw';
      }
      case CredReqEventStatus.AdditionalInformationRequested:
      case CredReqEventStatus.SecondAttemptMissingInfo:
      case CredReqEventStatus.ThirdAttemptMissingInfo: {
        return 'icon-alert-triangle';
      }
      case CredReqEventStatus.CancellationRequested:
      case CredReqEventStatus.Cancelled:
      case CredReqEventStatus.UnableToComplete: {
        return 'icon-x-circle';
      }
      case CredReqEventStatus.Completed:
      case CredReqEventStatus.CompletedWithConcern: {
        return 'icon-file-text';
      }
      default: {
        return 'icon-check';
      }
    }
  }

  public getMostRecentStartDate(): string {
    const processingStatus = [
      CredReqEventStatus.RequestInProgress,
      CredReqEventStatus.AdditionalInformationSubmitted
    ];
    const sortedEvents = this.allEvents
      .filter((event) => processingStatus.includes(event.status))
      .sort((a, b) => new Date(b.timestamp.getRawString()) - new Date(a.timestamp.getRawString()));

    if (sortedEvents.length === 0) {
      return '';
    }

    return sortedEvents.first()?.timestamp?.getRawString();
  }
}
